import {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import CollapseHeader from "../../../core/common/collapse-header";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { LeadService, Lead, LeadRequest } from "./lead.service";
import LeadForm from "./leadForm";
import { LeadFormDataProvider } from "./leadFormDataContext";
import { all_routes } from "../../router/all_routes";
import { useLeads } from "./useLeads";
import {
  showSuccessAlert,
  showErrorAlert,
  showConfirmationAlert,
} from "../../uiInterface/base-ui/alertservice";
import {
  getLifecycleTypePluralName,
  getLifecycleTypeName,
} from "../../../utils/lifecycleUtils";
import { AuthContext } from "../../../context/authcontext";
import { useTenantChange } from "../../../hooks/useTenantChange";
import {
  LeadKanbanService,
  KanbanLead,
  KanbanLeadStage,
} from "./leadKanban.service";
import TableFilters, {
  FilterState,
  FilterConfig,
} from "../../../components/filters/TableFilters";
import { getAuthHeaders } from "../../../utils/authUtils";
import {
  getStageDotClass,
  getStageColorValue,
} from "../../../utils/stageColors";
import ExportComponent from "../../../components/ExportComponent";
import LifecycleFilterDropdown from "../components/LifecycleFilterDropdown";
import ListFilterChips, { getClearedListFilters } from "../components/ListFilterChips";

const MySwal = withReactContent(Swal);



interface KanbanColumn extends KanbanLeadStage {
  leadCount: number;
  totalAmount: number;
}

interface LeadGridProps {
  onViewSwitch?: (view: 'list' | 'kanban') => void;
}

const LeadGrid = ({ onViewSwitch }: LeadGridProps) => {
  const route = all_routes;
  const navigate = useNavigate();
  const { selectedTenantId } = useContext(AuthContext);

  const {
    leads,
    loading,
    error,
    pagination,
    addLead,
    updateLead,
    removeLead,
    fetchLeads,
    isInitialized,
    clearLeadsData,
  } = useLeads();
  const [data, setData] = useState<Lead[]>([]);

  const [openModal, setOpenModal] = useState(false);
  const [openModal2, setOpenModal2] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [isUpdatingLead, setIsUpdatingLead] = useState(false);

  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [kanbanLoading, setKanbanLoading] = useState(true);
  const [kanbanError, setKanbanError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [allStages, setAllStages] = useState<KanbanLeadStage[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  useTenantChange({
    onTenantChange: () => {
      clearLeadsData();
      setData([]);
      setCurrentPage(1);
      setTotalCount(0);
      setAllStages([]);
    },
    clearData: clearLeadsData,
  });

  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    dateRange: [null, null] as [Date | null, Date | null],
    industryFilter: [],
    countryFilter: [],
    statusFilter: null,
    sortBy: "created_datetime",
    sortOrder: "desc",
    archivedFilter: "no",
  });

  const [filteredLeadsData, setFilteredLeadsData] = useState<any[]>([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [kanbanScrollLeft, setKanbanScrollLeft] = useState(0);
  const [showFloatingScrollbar, setShowFloatingScrollbar] = useState(false);
  const [kanbanContainerWidth, setKanbanContainerWidth] = useState(0);
  const [kanbanContainerLeft, setKanbanContainerLeft] = useState(0);

  const filterConfig: FilterConfig = useMemo(
    () => ({
      showSearch: true,
      searchField: "title",
      showDateFilter: true,
      dateField: "created_datetime",
      showIndustryFilter: false,
      showCountryFilter: false,
      pageName: "lead" as const,
      defaultSort: {
        field: "created_datetime",
        order: "desc" as const,
      },
    }),
    []
  );

  const fetchKanbanData = async (page: number = 1, reset: boolean = true) => {
    if (!selectedTenantId) return;

    try {
      if (reset) {
        setKanbanLoading(true);
        setCurrentPage(1);
      } else {
        setLoadingMore(true);
      }

      const data = await LeadKanbanService.fetchKanbanData(
        parseInt(selectedTenantId),
        20,
        page,
        filters
      );

      const sortedStages = [...data.results.stages].sort(
        (a, b) => a.stage_order - b.stage_order
      );

      if (reset) {
        setAllStages(sortedStages);
        setTotalCount(data.count || 0);
        setCurrentPage(1);
      } else {
        setAllStages((prev) =>
          sortedStages.map((stage) => {
            const existing = prev.find(
              (p) =>
                p.lifecycle_type_stage_id === stage.lifecycle_type_stage_id
            );
            return {
              ...stage,
              leads: existing
                ? [...existing.leads, ...stage.leads]
                : stage.leads,
            };
          })
        );
        setCurrentPage(page);
      }

      setHasMore(!!data.next);
      setKanbanError(null);
    } catch (err) {
      console.error("Error fetching kanban data:", err);
      setKanbanError("Failed to load kanban data");
    } finally {
      setKanbanLoading(false);
      setLoadingMore(false);
    }
  };

  const flatAllLeads = useMemo(
    () => allStages.flatMap((stage) => stage.leads),
    [allStages]
  );

  const { filteredKanbanLeads, totalFilteredCount } = useMemo(() => {
    if (isFilterActive) {
      return {
        filteredKanbanLeads: filteredLeadsData,
        totalFilteredCount: filteredLeadsData.length,
      };
    }

    return {
      filteredKanbanLeads: flatAllLeads,
      totalFilteredCount: flatAllLeads.length,
    };
  }, [flatAllLeads, filteredLeadsData, isFilterActive]);

  const updateColumns = useCallback(() => {
    const kanbanColumns: KanbanColumn[] = allStages.map((stage) => {
      if (isFilterActive) {
        const stageLeads = filteredLeadsData.filter(
          (lead) =>
            lead.lifecycle_type_stage?.lifecycle_type_stage_id ===
            stage.lifecycle_type_stage_id
        );
        const totalAmount = stageLeads.reduce(
          (sum, lead) => sum + (lead.estimated_value || 0),
          0
        );

        return {
          ...stage,
          leads: stageLeads,
          leadCount: stageLeads.length,
          totalAmount,
        };
      }

      const totalAmount = stage.leads.reduce(
        (sum, lead) => sum + (lead.estimated_value || 0),
        0
      );

      return {
        ...stage,
        leadCount: stage.count,
        totalAmount,
      };
    });

    setColumns(kanbanColumns);
  }, [allStages, filteredLeadsData, isFilterActive]);

  const loadMore = async () => {
    if (!hasMore || loadingMore || isFilterActive) return;
    await fetchKanbanData(currentPage + 1, false);
  };

  useEffect(() => {
    fetchKanbanData();
  }, [selectedTenantId, filters]);

  useEffect(() => {
    updateColumns();
  }, [updateColumns]);

  useEffect(() => {
    const handleWindowScroll = () => {
      if (!hasMore || loadingMore || kanbanLoading || isFilterActive) return;
      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      if (scrollPosition >= documentHeight - 300) {
        loadMore();
      }
    };

    window.addEventListener("scroll", handleWindowScroll);
    return () => window.removeEventListener("scroll", handleWindowScroll);
  }, [hasMore, loadingMore, kanbanLoading, isFilterActive, currentPage]);

  useEffect(() => {
    const handleScroll = () => {
      const kanbanContainer = document.getElementById('kanban-container');
      if (kanbanContainer) {
        const { scrollLeft, scrollWidth, clientWidth } = kanbanContainer;
        const rect = kanbanContainer.getBoundingClientRect();
        setKanbanScrollLeft(scrollLeft);
        setShowFloatingScrollbar(scrollWidth > clientWidth);
        setKanbanContainerWidth(clientWidth);
        setKanbanContainerLeft(rect.left);
      }
    };

    const setupScrollHandlers = () => {
      const kanbanContainer = document.getElementById('kanban-container');
      if (kanbanContainer) {
        kanbanContainer.addEventListener('scroll', handleScroll);
        handleScroll();

        const resizeObserver = new ResizeObserver(handleScroll);
        resizeObserver.observe(kanbanContainer);

        window.addEventListener('resize', handleScroll);

        return () => {
          kanbanContainer.removeEventListener('scroll', handleScroll);
          window.removeEventListener('resize', handleScroll);
          resizeObserver.disconnect();
        };
      }
      return null;
    };

    if (columns.length > 0 && filteredKanbanLeads.length > 0) {
      const timeoutId = setTimeout(() => {
        const cleanup = setupScrollHandlers();
        if (cleanup) {
          return cleanup;
        }
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [columns, filteredKanbanLeads, loading, kanbanLoading]);

  useEffect(() => {
    if (columns.length === 0 || filteredKanbanLeads.length === 0) return;
    const measure = () => {
      const kanbanContainer = document.getElementById('kanban-container');
      if (!kanbanContainer) return;
      const { scrollLeft, scrollWidth, clientWidth } = kanbanContainer;
      const rect = kanbanContainer.getBoundingClientRect();
      setKanbanScrollLeft(scrollLeft);
      setShowFloatingScrollbar(scrollWidth > clientWidth);
      setKanbanContainerWidth(clientWidth);
      setKanbanContainerLeft(rect.left);
    };
    const rafId = requestAnimationFrame(measure);
    const t1 = setTimeout(measure, 150);
    const t2 = setTimeout(measure, 400);
    const t3 = setTimeout(measure, 800);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [columns, filteredKanbanLeads, loading, kanbanLoading]);

  const handleFloatingScrollbarScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const kanbanContainer = document.getElementById('kanban-container');
    if (kanbanContainer) {
      kanbanContainer.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  useEffect(() => {
    const editOffcanvas = document.getElementById("offcanvas_edit_lead");

    const handleHidden = () => {
      setSelectedLead(null);
    };

    if (editOffcanvas) {
      editOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (editOffcanvas) {
        editOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  useEffect(() => {
    const addOffcanvas = document.getElementById("offcanvas_add_lead");

    const handleHidden = () => {
      setIsAddLeadOpen(false);
    };

    if (addOffcanvas) {
      addOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (addOffcanvas) {
        addOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  const handleCreate = async (lead: Partial<LeadRequest>) => {
    setIsCreatingLead(true);
    try {
      const newLead = await LeadService.createLead(lead);
      addLead(newLead);

      fetchKanbanData(1, true);
      closeOffcanvas("offcanvas_add_lead");

      setTimeout(() => {
        showSuccessAlert(`${getLifecycleTypeName("LEAD")} "${newLead.title}" created successfully!`);
      }, 500);
    } catch (err) {
      console.error("Failed to create lead:", err);
      showErrorAlert("Failed to create lead. Please try again.");
      throw err;
    } finally {
      setIsCreatingLead(false);
    }
  };

  const handleUpdate = async (id: number, lead: Partial<LeadRequest>) => {
    setIsUpdatingLead(true);
    try {
      const updatedLead = await LeadService.updateLead(id, lead);
      updateLead(updatedLead);

      fetchKanbanData(1, true);
      closeOffcanvas("offcanvas_edit_lead");

      setTimeout(() => {
        showSuccessAlert(`Lead "${updatedLead.title}" updated successfully!`);
      }, 500);
    } catch (err) {
      console.error("Failed to update lead:", err);
      showErrorAlert("Failed to update lead. Please try again.");
      throw err;
    } finally {
      setIsUpdatingLead(false);
    }
  };

  const handleDelete = async (lead: Lead) => {
    const result = await showConfirmationAlert(
      "Delete Lead",
      `Are you sure you want to delete "${lead.title}"? This action cannot be undone.`,
      async () => {
        if (lead.lead_id) {
          await LeadService.deleteLead(lead.lead_id);
        }
      }
    );

    if (result.isConfirmed) {
      if (lead.lead_id) {
        removeLead(lead.lead_id);
      }
      await showSuccessAlert(`Lead "${lead.title}" deleted successfully!`);
      fetchKanbanData(1, true);
    }
  };



  const handlePreview = (lead: Lead) => {
    navigate(`/leads/${lead.lead_id}`, {
      state: { leadData: lead },
    });
  };

  const handleKanbanLeadClick = (kanbanLead: KanbanLead) => {
    navigate(`/leads/${kanbanLead.lead_id}`);
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (!modal) return;
    const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(modal);
    if (bsOffcanvas) {
      bsOffcanvas.hide();
    } else {
      const closeButton = modal.querySelector('[data-bs-dismiss="offcanvas"]') as HTMLElement;
      if (closeButton) closeButton.click();
    }
  };

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters((prev: any) => ({
      ...prev,
      ...newFilters,
      archivedFilter: prev.archivedFilter,
    }));

    if (
      !newFilters.searchTerm &&
      !newFilters.dateRange[0] &&
      !newFilters.dateRange[1] &&
      newFilters.countryFilter.length === 0
    ) {
      setIsFilterActive(false);
      setFilteredLeadsData([]);
    }
  }, []);

  const handleDropdownFilterChange = useCallback((dropdownFilters: Record<string, any>) => {
    setFilters((prev: any) => ({
      ...prev,
      ...dropdownFilters,
    }));
    setIsFilterActive(false);
    setFilteredLeadsData([]);
  }, []);

  const applyListFilters = useCallback((nextFilters: Record<string, any>) => {
    setFilters(nextFilters as any);
    if (!nextFilters.searchTerm && !nextFilters.dateRange?.[0] && !nextFilters.dateRange?.[1]) {
      setIsFilterActive(false);
      setFilteredLeadsData([]);
    }
  }, []);

  const handleApiFilterChange = useCallback(
    (apiFilters: Record<string, any>) => {
      const newFilters: FilterState = {
        searchTerm: apiFilters.searchTerm || "",
        dateRange: apiFilters.dateRange || [null, null],
        industryFilter: apiFilters.industryFilter || [],
        countryFilter: apiFilters.countryFilter || [],
        statusFilter: apiFilters.statusFilter || null,
        sortBy: apiFilters.sortBy || "created_datetime",
        sortOrder: apiFilters.sortOrder || "desc",
      };
      setFilters((prev: any) => ({ ...prev, ...newFilters }));
    },
    []
  );

  const handleApiCall = useCallback(
    async (url: string) => {
      if (!selectedTenantId) {
        return;
      }

      try {
        setIsFilterLoading(true);

        const headers = getAuthHeaders();

        const response = await fetch(url, {
          method: "GET",
          headers: headers,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.results && Array.isArray(result.results)) {
          setFilteredLeadsData(result.results);
          setIsFilterActive(true);
        } else if (result.data && Array.isArray(result.data)) {
          setFilteredLeadsData(result.data);
          setIsFilterActive(true);
        } else if (Array.isArray(result)) {
          setFilteredLeadsData(result);
          setIsFilterActive(true);
        } else {
          setFilteredLeadsData([]);
          setIsFilterActive(true);
        }
      } catch (error: any) {
        console.error("Filter API error:", error);

        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("authCred");
          localStorage.removeItem("authResponse");
          localStorage.removeItem("authTimestamp");
          localStorage.removeItem("selectedTenantId");
          localStorage.removeItem("tenant_id");

          navigate("/login");
          return;
        }

        setFilteredLeadsData([]);
        setIsFilterActive(false);
      } finally {
        setIsFilterLoading(false);
      }
    },
    [selectedTenantId, navigate]
  );

  const handleFiltersReset = useCallback(() => {
    setFilters({
      searchTerm: "",
      dateRange: [null, null],
      industryFilter: [],
      countryFilter: [],
      statusFilter: null,
      sortBy: "created_datetime",
      sortOrder: "desc",
      archivedFilter: "no",
    });
    setIsFilterActive(false);
    setFilteredLeadsData([]);
  }, []);

  const formatCurrency = (amount: number, currencyCode: string = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getAvatarText = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getAvatarColor = (index: number) => {
    const colors = ["success", "warning", "info", "danger", "primary"];
    return colors[index % colors.length];
  };

  const onDragEnd = async (result: any) => {
    if (!result.destination || !selectedTenantId) return;

    const { source, destination } = result;

    if (source.droppableId === destination.droppableId) {
      const colIdx = columns.findIndex(
        (col) => col.lifecycle_type_stage_id.toString() === source.droppableId
      );
      const newLeads = Array.from(columns[colIdx].leads);
      const [removed] = newLeads.splice(source.index, 1);
      newLeads.splice(destination.index, 0, removed);

      const newColumns = [...columns];
      newColumns[colIdx] = { ...columns[colIdx], leads: newLeads };
      setColumns(newColumns);
    } else {
      const sourceColIdx = columns.findIndex(
        (col) => col.lifecycle_type_stage_id.toString() === source.droppableId
      );
      const destColIdx = columns.findIndex(
        (col) =>
          col.lifecycle_type_stage_id.toString() === destination.droppableId
      );

      const sourceLeads = Array.from(columns[sourceColIdx].leads);
      const destLeads = Array.from(columns[destColIdx].leads);

      const [removed] = sourceLeads.splice(source.index, 1);
      destLeads.splice(destination.index, 0, removed);

      const newColumns = [...columns];
      newColumns[sourceColIdx] = {
        ...columns[sourceColIdx],
        leads: sourceLeads,
        leadCount: sourceLeads.length,
        totalAmount: sourceLeads.reduce(
          (sum, lead) => sum + (lead.estimated_value || 0),
          0
        ),
      };
      newColumns[destColIdx] = {
        ...columns[destColIdx],
        leads: destLeads,
        leadCount: destLeads.length,
        totalAmount: destLeads.reduce(
          (sum, lead) => sum + (lead.estimated_value || 0),
          0
        ),
      };
      setColumns(newColumns);

      try {
        await LeadKanbanService.updateLeadStage(
          removed.lead_id,
          columns[destColIdx].lifecycle_type_stage_id,
          parseInt(selectedTenantId)
        );

        setAllStages((prev) =>
          prev.map((stage) => {
            if (
              stage.lifecycle_type_stage_id ===
              columns[sourceColIdx].lifecycle_type_stage_id
            ) {
              return {
                ...stage,
                leads: stage.leads.filter(
                  (lead) => lead.lead_id !== removed.lead_id
                ),
                count: Math.max(0, stage.count - 1),
              };
            }
            if (
              stage.lifecycle_type_stage_id ===
              columns[destColIdx].lifecycle_type_stage_id
            ) {
              return {
                ...stage,
                leads: [
                  ...stage.leads,
                  {
                    ...removed,
                    lifecycle_type_stage: {
                      ...(removed.lifecycle_type_stage || {}),
                      lifecycle_type_stage_id:
                        columns[destColIdx].lifecycle_type_stage_id,
                      stage_name: columns[destColIdx].stage_name,
                    },
                  },
                ],
                count: stage.count + 1,
              };
            }
            return stage;
          })
        );

        MySwal.fire({
          toast: true,
          position: "bottom-end",
          icon: "success",
          title: `Lead "${removed.title}" moved to ${columns[destColIdx].stage_name} successfully!`,
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } catch (error) {
        console.error("Error updating lead stage:", error);
        MySwal.fire({
          toast: true,
          position: "bottom-end",
          icon: "error",
          title: "Failed to update lead stage. Please try again.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });

        fetchKanbanData(1, true);
      }
    }
  };

  return (
    <>
      <style>{`
        #kanban-container::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <LeadFormDataProvider isMainApiCompleted={!kanbanLoading} shouldLoad={isAddLeadOpen || !!selectedLead}>
        <div className="page-wrapper">
          <div className="content">
            <div className="row">
              <div className="col-md-12">
                {/* Page Header */}
                <div className="page-header">
                  <div className="row align-items-center">
                    <div className="col-8">
                      <h4 className="page-title">
                        {getLifecycleTypeName("LEAD")}
                        <span className="count-title">{totalCount}</span>
                      </h4>
                    </div>
                    <div className="col-4 text-end">
                      <div className="head-icons">
                        <CollapseHeader />
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Page Header */}

                <div className="card">
                  <div className="card-header">
                    <div className="row align-items-center">
                      <div className="col">
                        <ListFilterChips
                          entityType="lead"
                          filters={filters}
                          onRemove={(patch) => applyListFilters({ ...filters, ...patch })}
                          onClearAll={() => applyListFilters(getClearedListFilters())}
                          disabled={loading}
                          className="mb-0"
                        />
                      </div>
                      <div className="col-sm-auto">
                        <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                          <ExportComponent entityType="lead" filters={filters} />
                          <button
                            className="btn btn-primary"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#offcanvas_add_lead"
                            onClick={() => setIsAddLeadOpen(true)}
                          >
                            <i className="ti ti-square-rounded-plus me-2" />
                            Add {getLifecycleTypeName("LEAD")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Filter Bar */}
                  <div className="card-body d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                    <TableFilters
                      config={filterConfig}
                      data={flatAllLeads}
                      onFilterChange={handleFilterChange}
                      onApiFilterChange={handleApiFilterChange}
                      onApiCall={handleApiCall}
                      onFiltersReset={handleFiltersReset}
                      searchTerm={filters.searchTerm}
                      dateRange={filters.dateRange}
                      hideClearButton
                      tenantId={selectedTenantId || undefined}
                      className="mt-4"
                    />
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <LifecycleFilterDropdown
                        entityType="lead"
                        onFilterChange={handleDropdownFilterChange}
                        currentFilters={filters}
                        loading={loading}
                      />
                      <div className="view-icons ms-3">
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            onViewSwitch?.('list');
                          }}
                        >
                          <i className="ti ti-list-tree" />
                        </Link>
                        <Link to="#" className="active">
                          <i className="ti ti-layout-kanban" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  {loading ? (
                    <div className="card-body">
                      <div className="text-center py-4">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">
                            Loading{" "}
                            {getLifecycleTypePluralName("LEAD").toLowerCase()}...
                          </small>
                        </div>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="card-body">
                      <div className="alert alert-danger text-center">
                        <i className="ti ti-alert-circle me-2"></i>
                        {error}
                      </div>
                    </div>
                  ) : (
                    <div className="card-body">
                      {kanbanLoading ? (
                        <div className="text-center py-5">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <div className="mt-2">
                            <small className="text-muted">
                              Loading {getLifecycleTypeName("LEAD")} Kanban...
                            </small>
                          </div>
                        </div>
                      ) : kanbanError ? (
                        <div className="alert alert-danger text-center">
                          <i className="ti ti-alert-circle me-2"></i>
                          {kanbanError}
                          <button
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={() => fetchKanbanData(1, true)}
                          >
                            Retry
                          </button>
                        </div>
                      ) : (
                        <>
                          <div style={{ position: "relative" }}>
                            {isFilterLoading && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  zIndex: 10,
                                  minHeight: "200px",
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <div
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </div>
                                  <span>Filtering results...</span>
                                </div>
                              </div>
                            )}
                            {filteredKanbanLeads.length === 0 ? (
                              <div className="text-center py-5">
                                <div className="avatar avatar-xl bg-light-gray rounded-circle mb-3 mx-auto">
                                  <i className="ti ti-chart-arcs fs-36 text-gray"></i>
                                </div>
                                <h4 className="text-gray mb-2">No {getLifecycleTypeName("LEAD")} Found!</h4>
                                <p className="text-muted mb-3">
                                  There are no {getLifecycleTypePluralName("LEAD").toLowerCase()} to display at the moment.
                                </p>
                                <button
                                  className="btn btn-primary"
                                  data-bs-toggle="offcanvas"
                                  data-bs-target="#offcanvas_add_lead"
                                  onClick={() => setIsAddLeadOpen(true)}
                                >
                                  <i className="fa fa-plus me-2" />
                                  Add Your First {getLifecycleTypeName("LEAD")}
                                </button>
                              </div>
                            ) : (
                              <DragDropContext onDragEnd={onDragEnd}>
                                <div
                                  id="kanban-container"
                                  className="d-flex overflow-x-auto align-items-start mb-0 gap-3"
                                  style={{
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none'
                                  }}
                                >
                                  {columns.map((col, colIdx) => (
                                    <div
                                      className="kanban-list-items p-2 rounded border"
                                      key={col.lifecycle_type_stage_id}
                                      style={{
                                        minWidth: 300,
                                        borderTop: `4px solid ${getStageColorValue(
                                          colIdx
                                        )}`,
                                      }}
                                    >
                                      <div className="card mb-0 border-0 shadow">
                                        <div className="card-body p-2">
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                              <h6 className="d-flex align-items-center mb-1">
                                                <i
                                                  className={`ti ti-circle-filled fs-10 ${getStageDotClass(
                                                    colIdx
                                                  )} me-1`}
                                                />
                                                {col.stage_name}
                                              </h6>
                                              <span>
                                                {col.leadCount}{" "}
                                                {getLifecycleTypePluralName("LEAD")}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <Droppable
                                        droppableId={col.lifecycle_type_stage_id.toString()}
                                      >
                                        {(provided: any) => (
                                          <div
                                            className="kanban-drag-wrap"
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            style={{
                                              minHeight: 100,
                                              borderTop: `4px solid ${getStageColorValue(
                                                colIdx
                                              )}`,
                                            }}
                                          >
                                            {col.leads.map((lead, idx) => (
                                              <Draggable
                                                draggableId={lead.lead_id.toString()}
                                                index={idx}
                                                key={lead.lead_id}
                                              >
                                                {(provided: any, snapshot: any) => (
                                                  <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                      ...provided.draggableProps
                                                        .style,
                                                      marginBottom: 16,
                                                      opacity: snapshot.isDragging
                                                        ? 0.8
                                                        : 1,
                                                    }}
                                                  >
                                                    <div
                                                      className="card kanban-card border mb-0 mt-3 shadow"
                                                      style={{
                                                        borderTop: `4px solid ${getStageColorValue(
                                                          colIdx
                                                        )}`,
                                                      }}
                                                    >
                                                      <div className="card-body">
                                                        <div className="d-block">
                                                          <div className="d-flex align-items-center mb-3">
                                                            <Link
                                                              to={`/leads/${lead.lead_id}`}
                                                              className={`avatar avatar-sm bg-primary rounded-circle flex-shrink-0 me-2`}
                                                              onClick={() =>
                                                                handleKanbanLeadClick(
                                                                  lead
                                                                )
                                                              }
                                                            >
                                                              <i
                                                                className="ti ti-chart-arcs"
                                                                style={{
                                                                  color: "#ffffff",
                                                                  fontSize: "1rem",
                                                                }}
                                                              />
                                                            </Link>
                                                            <h6 className="fw-medium fs-14 mb-0">
                                                              <Link
                                                                to={`/leads/${lead.lead_id}`}
                                                                onClick={() =>
                                                                  handleKanbanLeadClick(
                                                                    lead
                                                                  )
                                                                }
                                                              >
                                                                {lead.title?.length > 30 ? (
                                                                  <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip id={`tooltip-${lead.lead_id}`}>{lead.title}</Tooltip>}
                                                                  >
                                                                    <span>{`${lead.title.substring(0, 30)}...`}</span>
                                                                  </OverlayTrigger>
                                                                ) : (
                                                                  lead.title
                                                                )}
                                                              </Link>
                                                            </h6>
                                                          </div>
                                                        </div>
                                                        <div className="d-flex flex-column">
                                                          <p className="text-default d-inline-flex align-items-center mb-2">
                                                            <i className="ti ti-report-money text-dark me-1" />
                                                            {lead.estimated_value ||
                                                              "No Estimated Value"}{" "}
                                                            {lead.estimated_value
                                                              ? lead.currency_code
                                                              : " "}
                                                          </p>
                                                          <p className="text-default d-inline-flex align-items-center mb-2">
                                                            <i className="ti ti-building text-dark me-1" />
                                                            {lead.company?.company_name ? (
                                                              lead.company.company_name.length > 30 ? (
                                                                <OverlayTrigger
                                                                  placement="top"
                                                                  overlay={<Tooltip id={`tooltip-company-${lead.lead_id}`}>{lead.company.company_name}</Tooltip>}
                                                                >
                                                                  <span>{`${lead.company.company_name.substring(0, 30)}...`}</span>
                                                                </OverlayTrigger>
                                                              ) : (
                                                                lead.company.company_name
                                                              )
                                                            ) : (
                                                              "No Company"
                                                            )}
                                                          </p>
                                                          <p className="text-default d-inline-flex align-items-center mb-2">
                                                            <i className="ti ti-user text-dark me-1" />
                                                            {lead.primary_contact?.full_name ? (
                                                              lead.primary_contact.full_name.length > 30 ? (
                                                                <OverlayTrigger
                                                                  placement="top"
                                                                  overlay={<Tooltip id={`tooltip-contact-${lead.lead_id}`}>{lead.primary_contact.full_name}</Tooltip>}
                                                                >
                                                                  <span>{`${lead.primary_contact.full_name.substring(0, 30)}...`}</span>
                                                                </OverlayTrigger>
                                                              ) : (
                                                                lead.primary_contact.full_name
                                                              )
                                                            ) : (
                                                              "No Contact"
                                                            )}
                                                          </p>
                                                          <p className="text-default d-inline-flex align-items-center">
                                                            <i className="fa-solid fa-code-branch me-1" style={{ color: "rgba(71, 71, 88, 1)", fontSize: "12px", marginLeft: "3px", }}></i>
                                                            {lead.lifecycle_source?.lifecycle_source_display_name || (lead as any)?.source}
                                                          </p>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                )}
                                              </Draggable>
                                            ))}
                                            {provided.placeholder}
                                          </div>
                                        )}
                                      </Droppable>
                                    </div>
                                  ))}
                                </div>

                                {loadingMore && (
                                  <div className="text-center mt-4">
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    Loading more {getLifecycleTypePluralName("LEAD")}...
                                  </div>
                                )}

                                {showFloatingScrollbar && (
                                  <div
                                    style={{
                                      position: 'fixed',
                                      bottom: '5px',
                                      left: `${kanbanContainerLeft}px`,
                                      width: `${kanbanContainerWidth}px`,
                                      height: '12px',
                                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                      borderRadius: '6px',
                                      zIndex: 1000,
                                      padding: '2px',
                                      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '100%',
                                        height: '8px',
                                        overflowX: 'auto',
                                        overflowY: 'hidden'
                                      }}
                                      onScroll={handleFloatingScrollbarScroll}
                                    >
                                      <div
                                        style={{
                                          width: `${(document.getElementById('kanban-container')?.scrollWidth || 0)}px`,
                                          height: '1px'
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </DragDropContext>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add Lead Offcanvas */}
        <div
          className="offcanvas offcanvas-end"
          tabIndex={-1}
          id="offcanvas_add_lead"
          style={{ width: "550px" }}

        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">
              Add New {getLifecycleTypeName("LEAD")}
            </h5>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              onClick={() => setSelectedLead(null)}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="offcanvas-body">
            {isAddLeadOpen && (
              <LeadForm
                onSubmit={handleCreate}
                onCancel={() => {
                  closeOffcanvas("offcanvas_add_lead");
                }}
                isSubmitting={isCreatingLead}
              />
            )}
          </div>
        </div>

        {/* Edit Lead Offcanvas */}
        <div
          className="offcanvas offcanvas-end"
          tabIndex={-1}
          id="offcanvas_edit_lead"
          style={{ width: "550px" }}

        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">Edit {getLifecycleTypeName("LEAD")}</h5>
            <button
              type="button"
              className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="offcanvas-body">
            {selectedLead && (
              <LeadForm
                lead={selectedLead}
                onSubmit={(formData) => {
                  if (selectedLead?.lead_id) {
                    return handleUpdate(selectedLead.lead_id, formData);
                  }
                  return Promise.resolve();
                }}
                onCancel={() => {
                  closeOffcanvas("offcanvas_edit_lead");
                }}
                isMainApiCompleted={!kanbanLoading}
                isSubmitting={isUpdatingLead}
              />
            )}
          </div>
        </div>
      </LeadFormDataProvider>
    </>
  );
};

export default LeadGrid;
